import { ApolloError } from '@apollo/client';
import { ChatProspectStage } from '@/types';
import { useGeneratedSalesPortalProspectStageQuery } from '@/experiences/sales-portal/domain/prospects/screens/prospect-detail/blocs/documents/sales-portal-prospect-stage.generated';

interface ProspectStageLoaderParams {
  restaurantUUID: string;
  skip?: boolean;
  supplierUUID: string;
}

interface ProspectStageLoaderResult {
  error: ApolloError | undefined;
  loading: boolean;
  refetch: () => Promise<unknown>;
  stage: ChatProspectStage | null;
}

/**
 * Gets the pipeline stage of the prospect for the stage menu.
 *
 * @param params.restaurantUUID - The UUID of the restaurant.
 * @param params.skip - Skips the query when true.
 * @param params.supplierUUID - The UUID of the supplier.
 * @returns The stage, null until the query gives a result, along with a
 *   loading flag, an error and a refetch.
 */
export const useProspectStageLoader = ({
  restaurantUUID,
  skip,
  supplierUUID,
}: ProspectStageLoaderParams): ProspectStageLoaderResult => {
  const { data, error, loading, refetch } =
    useGeneratedSalesPortalProspectStageQuery({
      notifyOnNetworkStatusChange: true,
      skip,
      variables: { restaurantUUID, supplierUUID },
    });

  return {
    error,
    loading,
    refetch: () => refetch().catch(() => {}),
    stage: data?.prospectDetails.stage ?? null,
  };
};
